import type { AiEngineeringMetric, AiEngineeringReviewWorkflow } from "./aiEngineeringTypes";

export type ReviewWorkflowNodeTone = "accent" | "attention" | "cohort" | "muted" | "soft";

export type ReviewWorkflowGraphNode = {
  definition: string;
  emptyLabel?: string;
  id: string;
  label: string;
  observed: boolean;
  tone: ReviewWorkflowNodeTone;
  value: number | null;
};

export type ReviewWorkflowGraphLink = {
  id: string;
  kind: "finding-comment" | "pr";
  source: string;
  target: string;
  value: number;
};

export type ReviewWorkflowGraph = {
  links: ReviewWorkflowGraphLink[];
  nodes: ReviewWorkflowGraphNode[];
};

function count(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

function observed(metric: AiEngineeringMetric): boolean {
  return metric.state !== "unavailable";
}

function boundedNumerator(metric: AiEngineeringMetric, maximum: number): number {
  return Math.min(count(metric.numerator), maximum);
}

export function buildReviewWorkflowGraph(
  workflow: AiEngineeringReviewWorkflow,
): ReviewWorkflowGraph {
  const reviewObserved = observed(workflow.reviewCoverage);
  const eligiblePrs = reviewObserved ? count(workflow.reviewCoverage.denominator) : null;
  const reviewedPrs =
    eligiblePrs === null ? null : boundedNumerator(workflow.reviewCoverage, eligiblePrs);
  const noReviewPrs =
    eligiblePrs === null || reviewedPrs === null ? null : eligiblePrs - reviewedPrs;

  const findingsObserved = observed(workflow.findingCoverage) && reviewedPrs !== null;
  const prsWithFindings = findingsObserved
    ? boundedNumerator(workflow.findingCoverage, reviewedPrs ?? 0)
    : null;
  const prsWithoutFindings =
    reviewedPrs === null || prsWithFindings === null ? null : reviewedPrs - prsWithFindings;

  const actionObserved = observed(workflow.findingsActedOn) && prsWithFindings !== null;
  const changedAfterFinding = actionObserved
    ? boundedNumerator(workflow.findingsActedOn, prsWithFindings ?? 0)
    : null;
  const noChangeAfterFinding =
    prsWithFindings === null || changedAfterFinding === null
      ? null
      : prsWithFindings - changedAfterFinding;

  const nodes: ReviewWorkflowGraphNode[] = [
    {
      definition: "AI-assisted pull requests merged in the selected window.",
      emptyLabel: "No AI-assisted merges",
      id: "ai-assisted",
      label: "AI-assisted merged",
      observed: eligiblePrs !== null,
      tone: "cohort",
      value: eligiblePrs,
    },
    {
      definition: "AI-assisted merged PRs with an observed AI review request or tagged finding.",
      emptyLabel: "No review requests",
      id: "review-requested",
      label: "Review requested",
      observed: reviewedPrs !== null,
      tone: "accent",
      value: reviewedPrs,
    },
    {
      definition: "AI-assisted merged PRs with no observed AI review request.",
      emptyLabel: "No coverage drop-off",
      id: "no-review-request",
      label: "No review request",
      observed: noReviewPrs !== null,
      tone: "attention",
      value: noReviewPrs,
    },
    {
      definition: "Reviewed AI-assisted merged PRs with at least one structured finding tag.",
      emptyLabel: "No tagged findings",
      id: "tagged-finding",
      label: "Tagged finding",
      observed: prsWithFindings !== null,
      tone: "accent",
      value: prsWithFindings,
    },
    {
      definition: "Reviewed AI-assisted merged PRs without a structured finding tag.",
      emptyLabel: "Every reviewed PR had findings",
      id: "no-tagged-finding",
      label: "No tagged finding",
      observed: prsWithoutFindings !== null,
      tone: "muted",
      value: prsWithoutFindings,
    },
    {
      definition: "AI-assisted merged PRs that received a commit after their first tagged finding.",
      emptyLabel: "No follow-up commits",
      id: "changed-after-finding",
      label: "Commit after finding",
      observed: changedAfterFinding !== null,
      tone: "accent",
      value: changedAfterFinding,
    },
    {
      definition: "AI-assisted merged PRs with a finding and no later commit before merge.",
      emptyLabel: "Every finding was followed by a commit",
      id: "no-change-after-finding",
      label: "No later commit",
      observed: noChangeAfterFinding !== null,
      tone: "attention",
      value: noChangeAfterFinding,
    },
    ...workflow.dimensions.map(
      (dimension): ReviewWorkflowGraphNode => ({
        definition: `${dimension.dimension} finding comments on AI-assisted merged PRs. A PR can contribute more than one comment.`,
        emptyLabel: "No tagged comments",
        id: `finding-${dimension.dimension}`,
        label: dimension.dimension,
        observed: dimension.state !== "unavailable",
        tone: "soft",
        value: dimension.state === "unavailable" ? null : count(dimension.findings),
      }),
    ),
  ];

  const links: ReviewWorkflowGraphLink[] = [];
  const addLink = (
    source: string,
    target: string,
    value: number | null,
    kind: ReviewWorkflowGraphLink["kind"] = "pr",
  ) => {
    if (value === null || value <= 0) return;
    links.push({ id: `${kind}-${source}-${target}`, kind, source, target, value });
  };

  addLink("ai-assisted", "review-requested", reviewedPrs);
  addLink("ai-assisted", "no-review-request", noReviewPrs);
  addLink("review-requested", "tagged-finding", prsWithFindings);
  addLink("review-requested", "no-tagged-finding", prsWithoutFindings);
  addLink("tagged-finding", "changed-after-finding", changedAfterFinding);
  addLink("tagged-finding", "no-change-after-finding", noChangeAfterFinding);
  for (const node of nodes.filter((node) => node.id.startsWith("finding-"))) {
    addLink("tagged-finding", node.id, node.value, "finding-comment");
  }

  return { links, nodes };
}
